/** 레거시 `statusHelper.js` + clientInfo 목록 상태 표시 */
export const DRUG_APPROVAL_STATUS = {
  APPLIED: 'APPLIED',
  IN_REVIEW: 'IN_REVIEW',
  PENDING_TRANSFER: 'PENDING_TRANSFER',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  RESUBMITTED: 'RESUBMITTED',
  TERMINATED: 'TERMINATED',
} as const;

export type DrugApprovalStatus =
  (typeof DRUG_APPROVAL_STATUS)[keyof typeof DRUG_APPROVAL_STATUS];

/** 서버 fromKorean·상세 저장이 받는 제약사 거래 상태 */
export const PHARMA_TRANSACTION_STATUS_VALUES = [
  '신청',
  '확인 중',
  '이관 확인 중',
  '승인',
  '거절',
  '재신청',
  '종료',
] as const;

export type PharmaTransactionStatus =
  (typeof PHARMA_TRANSACTION_STATUS_VALUES)[number];

export const DRUG_APPROVAL_STATUS_OPTIONS: Array<{
  value: DrugApprovalStatus;
  label: PharmaTransactionStatus;
}> = [
  { value: DRUG_APPROVAL_STATUS.APPLIED, label: '신청' },
  { value: DRUG_APPROVAL_STATUS.IN_REVIEW, label: '확인 중' },
  { value: DRUG_APPROVAL_STATUS.PENDING_TRANSFER, label: '이관 확인 중' },
  { value: DRUG_APPROVAL_STATUS.APPROVED, label: '승인' },
  { value: DRUG_APPROVAL_STATUS.REJECTED, label: '거절' },
  { value: DRUG_APPROVAL_STATUS.RESUBMITTED, label: '재신청' },
  { value: DRUG_APPROVAL_STATUS.TERMINATED, label: '종료' },
];

/** 제약사 거래 상태 — 저장·Select value는 서버 한글(공백)과 동일 */
export const PHARMA_TRANSACTION_STATUS_OPTIONS = PHARMA_TRANSACTION_STATUS_VALUES.map(
  (status) => ({ value: status, label: status }),
);

export const formatStatusLabel = (status?: string | null): string => {
  if (!status) return '-';
  return status.replace(/_/g, ' ');
};

const DRUG_APPROVAL_LABELS: Record<string, string> = Object.fromEntries(
  DRUG_APPROVAL_STATUS_OPTIONS.map((opt) => [opt.value, opt.label]),
);

const KOREAN_BY_ENGLISH: Record<DrugApprovalStatus, PharmaTransactionStatus> =
  Object.fromEntries(
    DRUG_APPROVAL_STATUS_OPTIONS.map((opt) => [opt.value, opt.label]),
  ) as Record<DrugApprovalStatus, PharmaTransactionStatus>;

const ENGLISH_BY_KOREAN: Record<PharmaTransactionStatus, DrugApprovalStatus> =
  Object.fromEntries(
    DRUG_APPROVAL_STATUS_OPTIONS.map((opt) => [opt.label, opt.value]),
  ) as Record<PharmaTransactionStatus, DrugApprovalStatus>;

/** 서버·이력의 공백/밑줄/영문을 저장용 한글(공백)로 맞춘다 */
const TRANSACTION_STATUS_ALIASES: Record<string, PharmaTransactionStatus> = {
  ...KOREAN_BY_ENGLISH,
  신청: '신청',
  '확인 중': '확인 중',
  확인_중: '확인 중',
  확인중: '확인 중',
  '이관 확인 중': '이관 확인 중',
  이관_확인_중: '이관 확인 중',
  이관확인중: '이관 확인 중',
  승인: '승인',
  거절: '거절',
  재신청: '재신청',
  종료: '종료',
};

export const toPharmaTransactionStatusValue = (
  status?: string | null,
): PharmaTransactionStatus | '' => {
  if (!status) return '';
  return (
    TRANSACTION_STATUS_ALIASES[status] ??
    TRANSACTION_STATUS_ALIASES[status.replace(/_/g, ' ')] ??
    ''
  );
};

/**
 * 상세 GET이 영문·한글 어느 쪽이든 Select/저장용 enum으로 맞춘다.
 * PUT drugApprovalStatus는 Jackson enum 이름(APPLIED 등)을 받는다.
 */
export const toDrugApprovalStatusValue = (
  status?: string | null,
): DrugApprovalStatus | '' => {
  if (!status) return '';
  const upper = status.toUpperCase();
  if (upper in KOREAN_BY_ENGLISH) {
    return upper as DrugApprovalStatus;
  }
  const korean = toPharmaTransactionStatusValue(status);
  return korean ? ENGLISH_BY_KOREAN[korean] : '';
};

export const getDrugApprovalStatusLabel = (status?: string | null): string => {
  const english = toDrugApprovalStatusValue(status);
  if (!english) return status || '-';
  // 목록 Tag: 레거시 statusText(REJECTED) = 승인불가 / 상세 Select 옵션은 거절
  if (english === DRUG_APPROVAL_STATUS.REJECTED) return '승인불가';
  return DRUG_APPROVAL_LABELS[english] ?? status ?? '-';
};

export const mapDrugApprovalToTransactionStatus = (
  drugApprovalStatus: string,
): PharmaTransactionStatus | '' => {
  const english = toDrugApprovalStatusValue(drugApprovalStatus);
  return english ? KOREAN_BY_ENGLISH[english] : '';
};

/**
 * 목록 라디오(한글 거래 상태) → 병원급 drugApprovalStatus.
 * 레거시 handleClassificationStatusChange와 동일.
 */
export const mapContractStatusToDrugApproval = (
  contractStatus: string,
): DrugApprovalStatus | '' => {
  return toDrugApprovalStatusValue(contractStatus);
};

/**
 * 목록 거래 상태 라디오로 검색한 행이 화면에 보이는 상태와 맞는지 판정한다.
 *
 * 서버(GET /admin/business-relationships)는 제약사 거래 상태 OR 의약품 승인 상태로 찾아서,
 * 제약사 승인·의약품 거절인 병원급 행도 "승인" 검색 결과에 섞여 온다.
 * 목록은 병원급이면 의약품 승인 상태, 의원급이면 제약사 거래 상태를 보여 주므로 같은 기준으로 거른다.
 */
export const matchesContractStatus = (
  client: {
    clinicType?: string | null;
    transactionStatus?: string | null;
    drugApprovalStatus?: string | null;
  },
  contractStatus: string,
): boolean => {
  const selected = toPharmaTransactionStatusValue(contractStatus);
  if (!selected) return true;
  if (client.clinicType === 'HOSPITAL') {
    return (
      toDrugApprovalStatusValue(client.drugApprovalStatus) ===
      ENGLISH_BY_KOREAN[selected]
    );
  }
  return toPharmaTransactionStatusValue(client.transactionStatus) === selected;
};

export const getClinicTypeLabel = (clinicType?: string | null): string => {
  if (clinicType === 'HOSPITAL') return '병원급';
  if (clinicType === 'CLINIC') return '의원급';
  return clinicType || '-';
};

/** 레거시 Tag color — 병원급 drugApprovalStatus */
export const getDrugApprovalStatusColor = (
  status?: string | null,
): string => {
  switch (toDrugApprovalStatusValue(status)) {
    case DRUG_APPROVAL_STATUS.APPLIED:
    case DRUG_APPROVAL_STATUS.RESUBMITTED:
      return '#77B6FF';
    case DRUG_APPROVAL_STATUS.APPROVED:
      return '#22c55e';
    case DRUG_APPROVAL_STATUS.IN_REVIEW:
      return '#6e4aff';
    case DRUG_APPROVAL_STATUS.PENDING_TRANSFER:
      return '#FF7E7E';
    case DRUG_APPROVAL_STATUS.REJECTED:
      return '#ef4444';
    case DRUG_APPROVAL_STATUS.TERMINATED:
      return '#C6C6C6';
    default:
      return '#94a3b8';
  }
};

/** 레거시 Tag color — 의원급 transactionStatus (공백/밑줄 모두) */
export const getTransactionStatusColor = (
  status?: string | null,
): string => {
  const key = toPharmaTransactionStatusValue(status) || formatStatusLabel(status);
  const colorMap: Record<string, string> = {
    신청: '#77B6FF',
    승인: '#22c55e',
    '확인 중': '#6e4aff',
    '이관 확인 중': '#FF7E7E',
    재신청: '#f97316',
    거절: '#ef4444',
    종료: '#C6C6C6',
  };
  return colorMap[key] ?? '#94a3b8';
};
